import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * تسجيل صوتي في المتصفح.
 *
 * `MediaRecorder` يُنتج `audio/webm` في كروم وفَيَرفُكس، وهو ما أُضيف إلى
 * القائمة البيضاء للمرفقات. ولا مكتبة: التسجيل والتشغيل كلاهما في المتصفح
 * أصلًا، ومكتبةٌ لأجلهما تزيد الحزمة بلا مقابل.
 *
 * ## ما يجب أن يُطفأ
 *
 * الميكروفون يبقى مفتوحًا بعد التسجيل ما لم تُوقَف مسارات التدفّق صراحةً —
 * ومؤشّر التسجيل يبقى مضاءً في تبويب المتصفح، فيظنّ المستخدم أن النظام
 * يستمع إليه. الإطفاء في كل مخرج: عند الإيقاف، وعند الإلغاء، وعند إزالة
 * المكوّن.
 */

type State = 'idle' | 'asking' | 'recording' | 'ready';

/** أقصى مدة للتسجيل بالثواني — تسجيلٌ أطول يُكتب لا يُقال. */
const MAX_SECONDS = 180;

export function VoiceRecorder({
  onReady,
  onClear,
  disabled = false,
}: {
  /** يُستدعى حين يجهز التسجيل — الملف جاهز للرفع. */
  onReady: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const [state, setState] = useState<State>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef(0);

  /** إطفاء الميكروفون والمؤقّت — يُستدعى من كل مخرج. */
  const teardown = useCallback(() => {
    window.clearInterval(timer.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }, []);

  useEffect(() => teardown, [teardown]);

  /* عنوان المعاينة مورد: يُبطَل عند استبداله وعند الخروج. */
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const start = async () => {
    setError(null);

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('متصفحك لا يدعم التسجيل الصوتي. اكتب ملاحظة نصية بدلًا منه.');
      return;
    }

    setState('asking');
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = media;

      const rec = new MediaRecorder(media);
      chunks.current = [];
      recorder.current = rec;

      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      rec.onstop = () => {
        teardown();
        const blob = new Blob(chunks.current, { type: 'audio/webm' });

        /*
         * تسجيلٌ فارغ لا يُسلَّم.
         *
         * ضغطةٌ سريعة على «سجّل» ثم «أوقف» تُنتج ملفًا ببضع بايتات لا صوت
         * فيه — يُرفع ويُخزَّن ويُشغَّل صامتًا.
         */
        if (blob.size < 1024) {
          setError('التسجيل قصير جدًا. اضغط مطوّلًا ثم تكلّم.');
          setState('idle');
          setSeconds(0);
          return;
        }

        const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
        onReady(new File([blob], `ملاحظة صوتية ${stamp}.webm`, { type: 'audio/webm' }));
        setPreview(URL.createObjectURL(blob));
        setState('ready');
      };

      rec.start();
      setState('recording');
      setSeconds(0);

      timer.current = window.setInterval(() => {
        setSeconds((s) => {
          /* السقف يوقف التسجيل نفسه: عدّادٌ يتجاوزه بلا أثر ليس سقفًا. */
          if (s + 1 >= MAX_SECONDS) {
            recorder.current?.stop();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      teardown();
      setState('idle');
      setError('تعذّر الوصول إلى الميكروفون. تحقّق من إذن المتصفح.');
    }
  };

  const stop = () => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
  };

  const clear = () => {
    teardown();
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setSeconds(0);
    setState('idle');
    onClear();
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div className="voice">
      {error && <div className="alert alert-danger mb-2">{error}</div>}

      {state === 'idle' && (
        <button type="button" className="btn btn-sm" onClick={start} disabled={disabled}>
          <Icon name="chat" size={14} />
          تسجيل صوتي
        </button>
      )}

      {state === 'asking' && <span className="tiny mute">بانتظار إذن الميكروفون…</span>}

      {state === 'recording' && (
        <div className="voice-live">
          <span className="voice-dot" aria-hidden="true" />
          <span className="num">{mmss}</span>
          <button type="button" className="btn btn-sm btn-danger" onClick={stop}>
            إيقاف
          </button>
          <span className="tiny mute">الحدّ ثلاث دقائق</span>
        </div>
      )}

      {state === 'ready' && preview && (
        <div className="voice-live">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- ملاحظة داخلية لا مادة منشورة. */}
          <audio className="att-audio" src={preview} controls preload="metadata" />
          <span className="num tiny">{mmss}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={clear}>
            <Icon name="trash" size={14} />
            حذف
          </button>
        </div>
      )}
    </div>
  );
}
